"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { razorpayService } from "../../services/razorpayService"
import { Smartphone, CreditCard, Wallet, Loader2, CheckCircle, XCircle } from "lucide-react"
import toast from "react-hot-toast"

interface RazorpayPaymentProps {
  amount: number
  orderId: string
  userId: string
  mobileNumber?: string
  onPaymentSuccess: (transactionId: string, merchantTransactionId: string) => void
  onPaymentFailure: (error: string) => void
}

const RazorpayPayment: React.FC<RazorpayPaymentProps> = ({
  amount,
  orderId,
  userId,
  mobileNumber,
  onPaymentSuccess,
  onPaymentFailure,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<"idle" | "processing" | "success" | "failed">("idle")

  useEffect(() => {
    // Load Razorpay script
    const loadRazorpayScript = () => {
      const script = document.createElement("script")
      script.src = "https://checkout.razorpay.com/v1/checkout.js"
      script.async = true
      script.onload = () => {}
      script.onerror = () => {
        console.error("Failed to load Razorpay script")
        toast.error("Payment service unavailable")
      }
      document.body.appendChild(script)
    }

    if (!window.Razorpay) {
      loadRazorpayScript()
    }
  }, [])

  const handlePaymentInitiation = async () => {
    if (!window.Razorpay) {
      toast.error("Payment service is loading. Please try again.")
      return
    }

    setIsProcessing(true)
    setPaymentStatus("processing")

    try {
      // Create Razorpay order
      const result = await razorpayService.createOrder({
        orderId,
        amount,
        userId,
        mobileNumber,
      })

      if (result.success && result.data) {
        // Get user data for prefill
        const user = JSON.parse(localStorage.getItem("user") || "{}")

        // Initialize Razorpay checkout
        const options: any = razorpayService.getCheckoutOptions(result.data, {
          name: user.displayName || "",
          email: user.email || "",
          mobileNumber: mobileNumber || "",
        })

        // Add success and failure handlers
        options.handler = async (response: any) => {
          try {
            // Verify payment
            const verificationResult = await razorpayService.verifyPayment({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            })

            if (verificationResult.success) {
              setPaymentStatus("success")
              await onPaymentSuccess(response.razorpay_payment_id, response.razorpay_order_id)
              toast.success("Payment completed successfully!")
              // Hard fallback: ensure customer lands on orders page even if parent navigation is interrupted.
              setTimeout(() => {
                if (window.location.pathname !== "/orders") {
                  window.location.assign("/orders")
                }
              }, 800)
            } else {
              setPaymentStatus("failed")
              onPaymentFailure("Payment verification failed")
              toast.error("Payment verification failed")
            }
          } catch (error) {
            console.error("Payment verification error:", error)
            setPaymentStatus("failed")
            onPaymentFailure("Payment verification failed")
            toast.error("Payment verification failed")
          }
        }

        options.modal = {
          ondismiss: () => {
            setPaymentStatus("idle")
            setIsProcessing(false)
            onPaymentFailure("Payment cancelled by user")
            toast.error("Payment was cancelled")
          },
        }

        const rzp = new window.Razorpay(options)

        rzp.on("payment.failed", (response: any) => {
          setPaymentStatus("failed")
          onPaymentFailure(response.error.description || "Payment failed")
          toast.error(response.error.description || "Payment failed")
        })

        rzp.open()
      } else {
        setPaymentStatus("failed")
        onPaymentFailure(result.error || "Order creation failed")
        toast.error(result.error || "Order creation failed")
      }
    } catch (error) {
      console.error("Payment initiation error:", error)
      setPaymentStatus("failed")
      onPaymentFailure("Payment service unavailable")
      toast.error("Payment service unavailable")
    } finally {
      setIsProcessing(false)
    }
  }

  const renderPaymentStatus = () => {
    if (paymentStatus === "success") {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h3>
          <p className="text-gray-600">Your order has been confirmed.</p>
        </div>
      )
    }

    if (paymentStatus === "failed") {
      return (
        <div className="text-center py-8">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Failed</h3>
          <p className="text-gray-600 mb-4">Please try again or contact support.</p>
          <button
            onClick={() => {
              setPaymentStatus("idle")
              setIsProcessing(false)
            }}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )
    }

    if (paymentStatus === "processing") {
      return (
        <div className="text-center py-8">
          <Loader2 className="w-16 h-16 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h3>
          <p className="text-gray-600 mb-4">Please complete the payment in the popup window</p>
          <p className="text-sm text-gray-500">Do not close this window</p>
        </div>
      )
    }

    return null
  }

  const statusContent = renderPaymentStatus()
  if (statusContent) {
    return <div className="bg-white rounded-xl p-6">{statusContent}</div>
  }

  return (
    <div className="space-y-6">
      {/* Payment Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Payment</h2>
        <p className="text-gray-600">Secure payment powered by Razorpay</p>
        <div className="mt-4 p-4 bg-primary-50 rounded-lg">
          <p className="text-2xl font-bold text-primary-600">₹{amount.toLocaleString()}</p>
          <p className="text-sm text-gray-600">Order ID: {orderId}</p>
        </div>
      </div>

      {/* Payment Methods Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Methods</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center space-x-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-700">UPI</span>
          </div>
          <div className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-700">Cards</span>
          </div>
          <div className="flex items-center space-x-2">
            <Wallet className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-gray-700">Wallets</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-orange-600 rounded"></div>
            <span className="text-sm text-gray-700">Net Banking</span>
          </div>
        </div>
      </div>

      {/* Payment Button */}
      <button
        onClick={handlePaymentInitiation}
        disabled={isProcessing}
        className="w-full bg-primary-600 text-white py-4 rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <span>Pay ₹{amount.toLocaleString()}</span>
          </>
        )}
      </button>

      {/* Security Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <span className="text-sm font-medium text-gray-900">Secure Payment</span>
        </div>
        <p className="text-xs text-gray-600">
          Your payment is secured with 256-bit SSL encryption. Razorpay ensures your financial information is protected.
        </p>
      </div>
    </div>
  )
}

// Add Razorpay to window interface
declare global {
  interface Window {
    Razorpay: any
  }
}

export default RazorpayPayment
